import React, { useState, useEffect, useRef } from "react";
import { TileCard, EmpButton as Button } from '@empowernow/ui';
import { FormPreview } from './FormPreview';

import { FormDefinition } from './types';

interface FormModalProps {
  form: FormDefinition;
  initialData?: Record<string, any> | null;
  parameterMapping?: Record<string, string>;
  prePopulatedData?: Record<string, string>;
  onClose: () => void;
  onSubmit?: (data: Record<string, any>) => Promise<void>;
}

export function FormModal({
  form,
  initialData,
  parameterMapping = {},
  prePopulatedData,
  onClose,
  onSubmit,
}: FormModalProps) {
  const [isVisible, setIsVisible] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  // Map grid data to form values based on parameter mapping
  let mappedValues = initialData
    ? mapGridDataToFormValues(initialData, parameterMapping)
    : {};

  if (prePopulatedData) {
    mappedValues = { ...prePopulatedData, ...mappedValues };
  }

  console.log('FormModal - initialData:', initialData);
  console.log('FormModal - prePopulatedData:', prePopulatedData);
  console.log('FormModal - mappedValues:', mappedValues);

  // Entrance animation
  useEffect(() => {
    // Trigger entrance animation after mount
    requestAnimationFrame(() => {
      setIsVisible(true);
    });
  }, []);

  // Handle click outside to close
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      handleClose();
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    // Wait for exit animation before calling onClose
    setTimeout(onClose, 200);
  };

  return (
    <div 
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        backdropFilter: isVisible ? 'blur(4px)' : 'blur(0px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px',
        opacity: isVisible ? 1 : 0,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <style>{`
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translateY(-40px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        @keyframes modalSlideOut {
          from {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
          to {
            opacity: 0;
            transform: translateY(-40px) scale(0.95);
          }
        }
      `}</style>
      
      <div 
        ref={modalRef}
        style={{
          width: '100%',
          maxWidth: '700px',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          animation: isVisible ? 'modalSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'modalSlideOut 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <TileCard theme="auto" elevation={2}>
          {/* Enhanced Header */}
          <div style={{
            padding: '20px 24px',
            borderBottom: '1px solid var(--border-color)',
            background: 'var(--card-header-bg)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <div style={{ flex: 1, paddingRight: '16px' }}>
              <h2 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: 600,
                color: 'var(--main-text)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '24px' }}>
                  {form.title?.toLowerCase().includes('create') ? '➕' : 
                   form.title?.toLowerCase().includes('edit') || form.title?.toLowerCase().includes('modify') ? '✏️' : 
                   form.title?.toLowerCase().includes('view') ? '👁️' : 
                   form.title?.toLowerCase().includes('delete') ? '🗑️' : '📝'}
                </span>
                {form.title || 'Form'}
              </h2>
              {form.description && (
                <p style={{
                  margin: '4px 0 0 32px',
                  fontSize: '13px',
                  color: 'var(--main-text-secondary)',
                  lineHeight: '1.4'
                }}>
                  {form.description}
                </p>
              )}
            </div>
            <Button
              onClick={handleClose}
              style={{ 
                background: 'transparent',
                border: 'none',
                color: 'var(--main-text-secondary)',
                padding: '8px',
                fontSize: '24px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                borderRadius: '4px',
                minWidth: '40px',
                minHeight: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--error-bg)';
                e.currentTarget.style.color = 'var(--error-color)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'var(--main-text-secondary)';
              }}
            >
              ✕
            </Button>
          </div>

          {/* Scrollable Form Content */}
          <div style={{ 
            padding: '24px',
            maxHeight: 'calc(90vh - 100px)',
            overflowY: 'auto',
            overflowX: 'hidden'
          }}>
            <FormPreview
              form={form}
              initialValues={mappedValues}
              onSubmit={async (data) => {
                if (onSubmit) {
                  await onSubmit(data);
                }
                handleClose();
              }}
              onCancel={handleClose}
            />
          </div>
        </TileCard>
      </div>
    </div>
  );
}

// Helper function to map grid data to form values based on parameter mapping
function mapGridDataToFormValues(
  gridData: Record<string, any>, 
  parameterMapping: Record<string, string>
): Record<string, any> {
  const mappedValues: Record<string, any> = {};
  
  Object.entries(parameterMapping).forEach(([formField, gridField]) => {
    if (gridData[gridField] !== undefined) {
      mappedValues[formField] = gridData[gridField];
    }
  });
  
  return mappedValues;
}
